import {
  DrawingState, drawing, JSONReadyDrawingData, jsonizeDrawingData, dejsonizeDrawingData,
  collectPictureIDsFromJSONReadyDrawingData, collectPictureIDsFromDrawingState, blankDrawingState,
  bottomLayerID,
} from '../drawing/reducers/drawing';
import {
  DrawingAction, isDrawingAction, RemoveLayerAction, removeLayer, isEditingDrawingAction,
} from '../drawing/actions/DrawingAction';
import {
  IntegratedDrawingAction, DISCARD_HISTORY,
} from '../actions/IntegratedDrawingAction';
import history, {
  HistoryState, initHistory, canUndo, canRedo, prevRecord, nextRecord, takeSnapshot,
  recordForAction, forEachSnapshot,
} from './history';
import HistoryAction from '../actions/HistoryAction';

interface DrawingStateSnapshot {
  readonly data: DrawingState;
  readonly page: number | null;
}

type DrawingStateHistory = HistoryState<DrawingStateSnapshot>;
const blankHistory: DrawingStateHistory = initHistory();
const maxHistoryRecords = 10;

type DrawingEditorIndex = [string, number | null];

export interface IntegratedDrawingState {
  readonly currentData: { readonly [pageID: string]: DrawingState };
  readonly histories: { readonly [editorKey: string]: DrawingStateHistory };
}

export const initIntegratedDrawingState = (): IntegratedDrawingState => ({
  currentData: {},
  histories: {},
});

export function createPageID(key: string, page: number | null) {
  // TODO : Create appropriate rule.
  return (page === null) ? key : `${key}-${page}`;
}

function visitDrawingStates(
  src: IntegratedDrawingState,
  callback: (data: DrawingState) => void,
) {
  const { currentData, histories } = src;

  for (const pageID in currentData) {
    callback(currentData[pageID]);
  }

  for (const editorKey in histories) {
    forEachSnapshot(histories[editorKey], item => callback(item.data));
  }
}

function visitPictureReferencesInIntegratedDrawingState(
  src: IntegratedDrawingState,
  callback: (id: string) => void,
) {
  visitDrawingStates(src, (data) => {
    for (const id of collectPictureIDsFromDrawingState(data)) {
      callback(id);
    }
  });
}

export function collectPictureIDsFromIntegratedDrawingState(src: IntegratedDrawingState) {
  const ids: { [id: string]: true } = {};
  visitPictureReferencesInIntegratedDrawingState(src, id => ids[id] = true);
  return Object.keys(ids);
}

export function canUndoDrawing(state: IntegratedDrawingState, index: DrawingEditorIndex) {
  const [activeEditorKey] = index;

  if (!(activeEditorKey in state.histories)) {
    return false;
  }

  return canUndo(state.histories[activeEditorKey]);
}

export function canRedoDrawing(state: IntegratedDrawingState, index: DrawingEditorIndex) {
  const [activeEditorKey] = index;

  if (!(activeEditorKey in state.histories)) {
    return false;
  }

  return canRedo(state.histories[activeEditorKey]);
}

export function getPrevDrawingPage(state: IntegratedDrawingState, index: DrawingEditorIndex) {
  const [activeEditorKey] = index;

  if (!(activeEditorKey in state.histories)) {
    throw new Error('Illegal state.');
  }

  return prevRecord(state.histories[activeEditorKey]).page;
}

export function getNextDrawingPage(state: IntegratedDrawingState, index: DrawingEditorIndex) {
  const [activeEditorKey] = index;

  if (!(activeEditorKey in state.histories)) {
    throw new Error('Illegal state.');
  }

  return nextRecord(state.histories[activeEditorKey]).page;
}

export interface JSONReadyIntegratedDrawingData {
  readonly currentData: { readonly [pageID: string]: JSONReadyDrawingData };
}

export function collectPictureIDsFromJSONReadyIntegratedDrawingData(
  src: JSONReadyIntegratedDrawingData,
) {
  const ids: { [id: string]: true } = {};

  for (const pageID in src.currentData) {
    for (const id of collectPictureIDsFromJSONReadyDrawingData(src.currentData[pageID])) {
      ids[id] = true;
    }
  }

  return Object.keys(ids);
}

export function jsonizeIntegratedDrawingData(
  src: IntegratedDrawingState,
): JSONReadyIntegratedDrawingData {
  const currentData: { [pageID: string]: JSONReadyDrawingData } = {};

  for (const pageID in src.currentData) {
    currentData[pageID] = jsonizeDrawingData(src.currentData[pageID]);
  }

  return { currentData };
}

export function dejsonizeIntegratedDrawingData(
  src: JSONReadyIntegratedDrawingData,
): IntegratedDrawingState {
  const currentData: { [pageID: string]: DrawingState } = {};

  for (const pageID in src.currentData) {
    currentData[pageID] = dejsonizeDrawingData(src.currentData[pageID]);
  }

  return {
    ...initIntegratedDrawingState(),
    currentData,
  };
}

function takeDrawingSnapshot(
  state: IntegratedDrawingState,
  index: DrawingEditorIndex,
): IntegratedDrawingState {
  const [activeEditorKey, activeEditorPage] = index;

  const histories: { [editorKey: string]: DrawingStateHistory } = { ...state.histories };
  const prevHistory = (activeEditorKey in histories) ?
    state.histories[activeEditorKey] : blankHistory;

  const pageID = createPageID(activeEditorKey, activeEditorPage);
  histories[activeEditorKey] = takeSnapshot(
    prevHistory,
    {
      data: pageID in state.currentData ? state.currentData[pageID] : blankDrawingState,
      page: activeEditorPage,
    },
    maxHistoryRecords,
  );

  return { ...state, histories };
}

function consumeDrawingSnapshot(
  state: IntegratedDrawingState,
  index: DrawingEditorIndex,
  snapshot: DrawingStateSnapshot,
): IntegratedDrawingState {
  const [activeEditorKey] = index;

  const currentData: { [pageID: string]: DrawingState } = { ...state.currentData };
  const pageID = createPageID(activeEditorKey, snapshot.page);
  if (!(pageID in currentData)) {
    throw new Error('Illegal state.');
  }

  currentData[pageID] = snapshot.data;
  return { ...state, currentData };
}

function applyDiscardHistory(state: IntegratedDrawingState, key: string): IntegratedDrawingState {
  const histories: { [editorKey: string]: DrawingStateHistory } = { ...state.histories };
  delete histories[key];
  return { ...state, histories };
}

export function removeActiveLayer(
  state: IntegratedDrawingState,
  index: DrawingEditorIndex,
): RemoveLayerAction | null {
  const [activeEditorKey, activeEditorPage] = index;

  const { currentData } = state;
  const pageID = createPageID(activeEditorKey, activeEditorPage);
  if (pageID in currentData) {
    const { activeLayer } = state.currentData[pageID];
    if (activeLayer !== bottomLayerID) {
      return removeLayer(activeLayer);
    }
  }

  return null;
}

export function drawingHistory(
  state: IntegratedDrawingState,
  index: DrawingEditorIndex,
  action: HistoryAction.Action,
): IntegratedDrawingState {
  const [activeEditorKey] = index;

  const snapshot = recordForAction(state.histories[activeEditorKey], action);
  const pageID = createPageID(activeEditorKey, snapshot.page);
  const nextHistories: { [editorKey: string]: DrawingStateHistory } = { ...state.histories };
  nextHistories[activeEditorKey] = history(
    state.histories[activeEditorKey],
    {
      data: (pageID in state.currentData) ? state.currentData[pageID] : blankDrawingState,
      page: snapshot.page,
    },
    action,
  );

  return consumeDrawingSnapshot({ ...state, histories: nextHistories }, index, snapshot);
}

export const integratedDrawing = (
  state: IntegratedDrawingState,
  index: DrawingEditorIndex,
  action: DrawingAction | IntegratedDrawingAction,
): IntegratedDrawingState => {
  if (isDrawingAction(action)) {
    // Delegate drawing actions.
    let intermediateState: IntegratedDrawingState = state;
    if (isEditingDrawingAction(action)) {
      intermediateState = takeDrawingSnapshot(intermediateState, index);
    }

    const [activeEditorKey, activeEditorPage] = index;
    const pageID = createPageID(activeEditorKey, activeEditorPage);
    const currentData: { [pageID: string]: DrawingState } = { ...intermediateState.currentData };
    currentData[pageID] = drawing(currentData[pageID], action);
    if (currentData[pageID] === intermediateState.currentData[pageID]) {
      return intermediateState;
    }

    return { ...intermediateState, currentData };
  }

  switch (action.type) {
    case DISCARD_HISTORY:
      return applyDiscardHistory(state, action.key);
  }
};
